import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Clipboard } from '@angular/cdk/clipboard';
import { TranslateService } from '@ngx-translate/core';
import { filter, map, Observable, of } from 'rxjs';
import { environment } from '../../environments/environment';
import { createQueryParams } from '../helpers';
import { DialogService } from './dialog.service';
import { NotificationService } from './notifications.service';
import { FiscalNotificationService } from './fiscal-notification.service';
import { getApiError } from '../components/warehouse/warehouse.utils';
import { isInvoiceExpired } from '../components/payments/payments.utils';
import {
  CardPaymentCreatePayload,
  CardPaymentsBlock,
  CardPaymentUpdatePayload,
  PaymentIntegration,
  PaymentInvoice,
  PaymentListItem,
  PaymentServiceRecord,
  PaymentServiceCreatePayload,
  PaymentServiceUpdatePayload,
  PaymentsPage,
  PaymentsQuery,
  PaymentTypeRecord,
  PaymentTypeCreatePayload,
  PaymentTypeUpdatePayload,
} from '../components/payments/payments.types';

/**
 * Payments of deals: its API (card payments, the general list, payment types and services) and the feedback of its
 * screens: translated toasts, confirmations and readable API errors.
 */
@Injectable({ providedIn: 'root' })
export class BoardPaymentService {
  private readonly http = inject(HttpClient);
  private readonly dialogService = inject(DialogService);
  private readonly notificationService = inject(NotificationService);
  private readonly fiscalNotifications = inject(FiscalNotificationService);
  private readonly translateService = inject(TranslateService);
  private readonly clipboard = inject(Clipboard);

  // #region payment types and services

  /** All types, inactive ones too, without `isActive`; a company without types gets the standard ones on the first request */
  readonly getPaymentTypes = (isActive?: boolean): Observable<PaymentTypeRecord[]> =>
    this.request('GET', 'board/payment-types', null, { is_active: isActive });
  readonly createPaymentType = (body: PaymentTypeCreatePayload): Observable<PaymentTypeRecord> =>
    this.request('POST', 'board/payment-types', body);
  /** `is_active: true` brings back a deactivated type */
  readonly updatePaymentType = (id: string, body: PaymentTypeUpdatePayload): Observable<PaymentTypeRecord> =>
    this.request('PUT', `board/payment-types/${id}`, body);
  /** Nothing is deleted: the type is deactivated, it stays in the payments it was used in */
  readonly deactivatePaymentType = (id: string): Observable<PaymentTypeRecord> =>
    this.request('DELETE', `board/payment-types/${id}`);

  /** All services, inactive ones too, without `isActive` */
  readonly getPaymentServices = (isActive?: boolean): Observable<PaymentServiceRecord[]> =>
    this.request('GET', 'board/payment-services', null, { is_active: isActive });
  readonly createPaymentService = (body: PaymentServiceCreatePayload): Observable<PaymentServiceRecord> =>
    this.request('POST', 'board/payment-services', body);
  readonly updatePaymentService = (id: string, body: PaymentServiceUpdatePayload): Observable<PaymentServiceRecord> =>
    this.request('PUT', `board/payment-services/${id}`, body);
  readonly deactivatePaymentService = (id: string): Observable<PaymentServiceRecord> =>
    this.request('DELETE', `board/payment-services/${id}`);
  /** Integrations a company can connect a service to */
  readonly getPaymentIntegrations = (): Observable<PaymentIntegration[]> =>
    this.request('GET', 'board/payment-services/integrations');

  // #endregion

  // #region payments

  /** Every card payments request returns the whole block: all payments, `paid_total` and `payment_status` */
  readonly getCardPayments = (cardId: string): Observable<CardPaymentsBlock> =>
    this.request('GET', `board/card/${cardId}/payments`);
  /** 400 PAYMENT_TYPE_INACTIVE / PAYMENT_SERVICE_INACTIVE for an inactive type or service */
  readonly createCardPayment = (cardId: string, body: CardPaymentCreatePayload): Observable<CardPaymentsBlock> =>
    this.request('POST', `board/card/${cardId}/payments`, body);
  /** Returns the block of the card of the payment; 409 CARD_PAYMENT_STATUS_CONFLICT when the status forbids the change */
  readonly updatePayment = (paymentId: string, body: CardPaymentUpdatePayload): Observable<CardPaymentsBlock> =>
    this.request('PUT', `board/payments/${paymentId}`, body);
  /** The general list, the newest payment dates first; payments of deleted cards are not in it */
  readonly getPayments = (query: PaymentsQuery): Observable<PaymentsPage<PaymentListItem>> =>
    this.request('GET', 'board/payments', null, query);

  // #endregion

  // #region feedback

  /** Copies the payment link of the invoice, an expired one too with a warning; false when it was not copied */
  copyInvoiceLink(invoice: PaymentInvoice): boolean {
    if (!this.clipboard.copy(invoice.link)) {
      this.fail('board_payments.invoice.copy_failed');
      return false;
    }
    if (isInvoiceExpired(invoice)) this.notificationService.showWarning('', this.t('board_payments.invoice.copied_expired'));
    else this.success('board_payments.invoice.copied');
    return true;
  }

  t(key: string, params?: Record<string, unknown>): string {
    return String(this.translateService.instant(key, params));
  }

  success(key: string, params?: Record<string, unknown>): void {
    this.notificationService.showSuccess('', this.t(key, params));
  }

  /** Toast of a failure with its own text */
  fail(key: string, params?: Record<string, unknown>): void {
    this.notificationService.showError('', this.t(key, params));
  }

  /** Toast of an API error; `contextKey` says what failed */
  error(error: unknown, contextKey?: string): void {
    if (this.fiscalNotifications.showError(error)) return;
    const message = this.errorMessage(error);
    this.notificationService.showError('', contextKey ? `${this.t(contextKey)}<br>${message}` : message);
  }

  /** Emits once the translated question is confirmed, right away when there is no question */
  confirm(key: string | null, params?: Record<string, unknown>): Observable<unknown> {
    if (!key) return of(true);
    return this.dialogService.openConfirm({ text: this.t(key, params), isTranslate: true }).pipe(filter(Boolean));
  }

  /** The code of the API error, to tell a status conflict from the rest */
  getErrorCode(error: unknown): string | undefined {
    return this.fiscalNotifications.readError(error).code;
  }

  /** Readable text of an API error: `board_payments.errors.<CODE>`, the default error for a code without a text */
  errorMessage(error: unknown): string {
    if (this.fiscalNotifications.isFiscalCode(this.getErrorCode(error))) return this.fiscalNotifications.errorMessage(error);
    const { code, details } = getApiError(error);
    return (code && this.find(`board_payments.errors.${code}`, details)) || this.t('errors.default_error');
  }

  // #endregion

  /** The translation of the key, null when there is no text for it */
  private find(key: string, params?: Record<string, unknown>): string | null {
    const text: unknown = this.translateService.instant(key, params);
    return typeof text === 'string' && text !== key ? text : null;
  }

  private request<T>(method: string, path: string, body?: unknown, query?: object): Observable<T> {
    return this.http
      .request<{ data: T }>(method, `${environment.baseURL}/${path}`, {
        body,
        params: query && createQueryParams(query),
      })
      .pipe(map((res) => res.data));
  }
}
